<!-- components/VersionUpdatePromptCoreUI.client.vue -->
<template>
  <!-- 1) 하단 고정 "Updated" 버튼: 업데이트 감지되면 표시 -->
  <div class="fixed bottom-4 right-4 z-[9998] flex gap-2 items-center">
    <CButton
      v-if="hasUpdate"
      color="danger"
      variant="outline"
      class="btn-updated"
      @click="openModal"
    >
      {{ $t("version.button.updated") || "Updated" }}
      <span v-if="meta?.sha" class="ms-2 text-xs opacity-75"
        >({{ meta.sha }})</span
      >
    </CButton>
    <CButton v-else color="primary" class="btn-updated" @click="openModal">
      {{ $t("version.button.info") || "Versions" }}
    </CButton>
  </div>

  <!-- 2) CoreUI 모달: 버튼 클릭 시에만 열림 -->
  <CModal
    v-model:visible="showModal"
    backdrop="static"
    :keyboard="false"
    alignment="center"
    :portal="true"
  >
    <CModalHeader>
      <CModalTitle v-if="hasUpdate">
        {{ $t("common.guide.new_version_is_released") }}
      </CModalTitle>
      <CModalTitle v-else>
        {{ $t("version.button.info") }}
      </CModalTitle>
    </CModalHeader>

    <CModalBody>
      <p v-if="hasUpdate" class="text-sm text-gray-700">
        {{ $t("common.confirm.apply_new_version") }}<br />
        <span v-if="meta.sha" class="ml-1 text-xs text-gray-400">
          Frontend : {{ meta.sha }} - {{ meta.time }} </span
        ><br />
        <span class="ml-1 text-xs text-gray-400">
          Backend : {{ backendMeta.sha }} - {{ backendMeta.time }}
        </span>
      </p>
      <p v-else>
        <span class="ml-1 text-xs text-gray-400">
          Frontend : {{ frontendMeta.sha }} - {{ frontendMeta.time }} </span
        ><br />
        <span class="ml-1 text-xs text-gray-400">
          Backend : {{ backendMeta.sha }} - {{ backendMeta.time }}
        </span>
      </p>
    </CModalBody>

    <CModalFooter v-if="hasUpdate">
      <CButton color="secondary" variant="outline" @click="closeModal">
        {{ $t("version.button.later") }}
      </CButton>
      <CButton color="primary" @click="refresh">
        {{ $t("version.button.update") }}
      </CButton>
    </CModalFooter>
    <CModalFooter v-else>
      <CButton color="secondary" variant="outline" @click="closeModal">
        {{ $t("common.button.close") }}
      </CButton>
    </CModalFooter>
  </CModal>
</template>
<script setup lang="ts">
import {
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CModalTitle,
  CButton,
} from "@coreui/vue";
import { ref, onMounted, onBeforeUnmount } from "vue";

const hasUpdate = useState<boolean>("__version_has_update__");
const meta = useState<{ time?: string; sha?: string } | null>(
  "__version_meta__",
);

// 안전 파서
function toIsoOrNull(raw?: string | null): string | null {
  if (!raw) {
    return null;
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return null;
  }
  // 초/밀리초 판별 (밀리초면 1e12 이상일 가능성이 큼)
  const ms = n >= 1e12 ? n : n * 1000;
  const d = new Date(ms);
  if (isNaN(d.getTime())) {
    return null;
  }
  try {
    return d.toISOString();
  } catch {
    return null;
  }
}

const frontendMeta = ref<{ sha: string | null; time: string | null }>({
  sha: null,
  time: null,
});

const backendMeta = ref<{ sha: string | null; time: string | null }>({
  sha: null,
  time: null,
});

// 클라이언트에서만 로드 & 갱신
function loadFromStorage() {
  try {
    const fSha = localStorage.getItem("frontendCommitVersion");
    const fTime = localStorage.getItem("frontendBuildTime");
    const bSha = localStorage.getItem("backendCommitVersion");
    const bTime = localStorage.getItem("backendBuildTime");

    frontendMeta.value = {
      sha: fSha || null,
      time: toIsoOrNull(fTime) ?? fTime ?? null, // 이미 ISO면 그대로 노출
    };
    backendMeta.value = {
      sha: bSha || null,
      time: toIsoOrNull(bTime) ?? bTime ?? null,
    };
  } catch {
    // storage 접근 불가 시 무시
  }
}

onMounted(() => {
  loadFromStorage();
  // 다른 탭/창에서 값 바뀌면 동기화 (선택)
  window.addEventListener("storage", (e) => {
    if (
      e.key === "frontendCommitVersion" ||
      e.key === "frontendBuildTime" ||
      e.key === "backendCommitVersion" ||
      e.key === "backendBuildTime"
    ) {
      loadFromStorage();
    }
  });
});

onBeforeUnmount(() => {
  window.removeEventListener?.("storage", loadFromStorage as any);
});

// 모달 제어
const showModal = ref(false);
function openModal() {
  showModal.value = true;
}
function closeModal() {
  showModal.value = false;
}

// 새로고침 (클라이언트 보장)
function refresh() {
  window.location.reload();
}
</script>

<style scoped>
.btn-updated {
  margin-left: 20px;
}
</style>
